import type { PromotionDecisionResponse, PromotionsResponse } from "@starsach/shared-types";
import { Link, useParams } from "react-router-dom";
import { useAccess, useCustomer, useDecidePromotion, useProfiles, usePromotions } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { NotHeld } from "../../components/NotHeld";
import { EnvironmentGate, useEnvironmentName, useRememberEnvironment } from "../../components/Environment";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, MutationError, PageHeader, RelativeTime, StatusPill, btnPrimary, btnSecondary } from "../../components/ui";
import { moduleById } from "../../lib/modules";
import { viewAsUrl } from "../../viewAs";
import { ContextBar } from "./ContextBar";

const TONE = { pending_approval: "warning", applied: "ok", rejected: "critical" } as const;

/**
 * Every change request into this environment (ADR-0008): a promotion, a
 * gated direct edit, a rollback or an alias change (#481) waiting for a manager, and what was
 * decided before. Approving is the manager-only act; the API refuses
 * anyone else, and says why. Gairm staff decide only from inside the
 * customer's portal with editing enabled (#371): here, in their own
 * portal, the page is read-only and says where to go.
 */
export function PromotionsPage() {
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="promotions" />;
  return <Promotions environmentId={environmentId} />;
}

function Promotions({ environmentId }: { environmentId: string }) {
  const env = useCustomer(environmentId);
  const profiles = useProfiles(environmentId);
  const q = usePromotions(environmentId);
  const decide = useDecidePromotion(environmentId);
  const envName = env.data?.customer.friendly_name ?? environmentId;
  const envNameOf = useEnvironmentName();
  const nameOf = (profileId: string) => profiles.data?.profiles.find((p) => p.profileId === profileId)?.name ?? profileId;
  const pending = (q.data?.promotions ?? []).filter((p) => p.status === "pending_approval");
  // ADR-0017: approval is a verb, not a role; a read-only view-as tab holds the viewer's set, which lacks it.
  const access = useAccess();
  const canDecide = access.can(environmentId, "configuration.promotions:approve");
  const { me } = useAuth();
  // A reader cannot enable editing, so is not pointed at it; they get the plain "not held".
  const staffOwnPortal = me?.role === "internal-team" && !me.viewAs && access.staffRole !== undefined && access.staffRole !== "gairm-reader";
  const organisationId = env.data?.customer.organisation_id;
  // The request just decided: it stays in the list (decided), so its profile and kind are still there to name.
  const decided = decide.variables && q.data?.promotions.find((p) => p.promotionId === decide.variables.promotionId);

  return (
    <>
      <PageHeader title="Flow configuration · Promotions" />
      <div className="grid gap-5">
        <ContextBar environmentId={environmentId} environmentName={envName} live={env.data?.customer.live ?? false} onEnvironment={(id) => `/flow-configuration/${id}/promotions`}>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {q.data ? `${pending.length} waiting for a decision` : ""}
            {env.data && !env.data.customer.changes_require_approval && " · this environment does not gate changes"}
          </span>
        </ContextBar>
        {q.isPending && <LoadingState label="Loading change requests" />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {/* #478: outside the waiting card, which goes when the last request is decided - the outcome must outlive it. */}
        {decide.isSuccess && (
          <p role="status" className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-900 dark:border-green-900 dark:bg-green-950 dark:text-green-100">
            {decisionOutcome(decide.data, decided, envName, envNameOf, nameOf)}
          </p>
        )}
        <MutationError of={decide} />
        {q.data && (
          <>
            {pending.length > 0 && (
              <Card title="Waiting for a decision">
                <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {pending.map((p) => (
                    <li key={p.promotionId} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <div className="text-sm">
                        <strong>{nameOf(p.profileId)}</strong> · {describeRequest(p, envNameOf(p.sourceEnvironmentId), nameOf)} {p.binding ? "in" : "into"} {envName}
                        <span className="block text-xs text-neutral-500">
                          {p.requestedBy} · <RelativeTime iso={p.requestedAt} /> ·{" "}
                          {p.binding ? (
                            <Link to={`/flow-configuration/${environmentId}/aliases`} className="underline">see Aliases</Link>
                          ) : (
                            <Link to={`/flow-configuration/${environmentId}/profiles/${p.profileId}/promote`} className="underline">see the diff</Link>
                          )}
                        </span>
                      </div>
                      {!staffOwnPortal && (
                        <div className="flex gap-2">
                          <button type="button" className={btnPrimary} disabled={decide.isPending || !canDecide} onClick={() => decide.mutate({ promotionId: p.promotionId, approve: true })}>
                            Approve and apply to {envName}
                          </button>
                          <button type="button" className={btnSecondary} disabled={decide.isPending || !canDecide} onClick={() => decide.mutate({ promotionId: p.promotionId, approve: false })}>
                            Reject
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {staffOwnPortal ? (
                  <p role="note" className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
                    Deciding is the customer's managers' job. To decide for them, open{" "}
                    {organisationId ? (
                      // rel="opener" as on the customer page: a view-as tab takes its session from this one.
                      <a href={viewAsUrl(organisationId, `/flow-configuration/${environmentId}/promotions`)} target="_blank" rel="opener" className="underline">
                        View their portal
                      </a>
                    ) : (
                      "View their portal"
                    )}{" "}
                    › Enable editing and decide there. It is recorded under your name, acting for them.
                  </p>
                ) : (
                  !canDecide && <NotHeld permission="configuration.promotions:approve" className="mt-3 block" />
                )}
              </Card>
            )}
            <Card title="Every change request">
              <DataTable
                caption={`Change requests into ${envName}, newest first`}
                rows={q.data.promotions}
                rowKey={(p) => p.promotionId}
                empty={<EmptyState title="No change requests yet" hint="A promotion, a gated edit, a rollback or an alias change into this environment lands here, with what was decided." />}
                columns={[
                  { key: "profile", header: "Profile", render: (p) => <Link to={`/flow-configuration/${environmentId}/profiles/${p.profileId}`} className="underline">{nameOf(p.profileId)}</Link> },
                  { key: "what", header: "What", render: (p) => describeRequest(p, envNameOf(p.sourceEnvironmentId), nameOf) },
                  { key: "status", header: "Status", render: (p) => <StatusPill status={p.status.replace("_", " ")} tone={TONE[p.status]} /> },
                  { key: "requested", header: "Requested", render: (p) => <>{p.requestedBy} · <RelativeTime iso={p.requestedAt} /></> },
                  { key: "decided", header: "Decided", render: (p) => (p.decidedAt ? <>{p.decidedBy} · <RelativeTime iso={p.decidedAt} />{p.appliedVersion ? ` · v${p.appliedVersion}` : ""}</> : "-") },
                ]}
              />
            </Card>
          </>
        )}
      </div>
    </>
  );
}

type Promotion = PromotionsResponse["promotions"][number];

/** #478: what was decided, on which profile, and where it now stands in this environment. */
function decisionOutcome(
  result: PromotionDecisionResponse,
  request: Promotion | undefined,
  envName: string,
  envNameOf: (environmentId: string) => string,
  nameOf: (profileId: string) => string,
): string {
  const what = request ? `${nameOf(request.profileId)} · ${describeRequest(request, envNameOf(request.sourceEnvironmentId), nameOf)}` : "the change request";
  if (result.status === "rejected") return `Rejected ${what}. Nothing was applied to ${envName}.`;
  if ("binding" in result) return `Approved ${what}. Applied: ${result.binding.alias} ${result.binding.op === "bind" ? "is bound" : "is removed"} in ${envName}.`;
  return `Approved ${what}. Applied as v${result.configVersion} in ${envName}.`;
}

/** #481: a binding request says which alias and what it would do, against the profile named beside it. */
function describeRequest(p: Promotion, source: string, nameOf: (profileId: string) => string): string {
  if (p.binding) {
    if (p.binding.op === "unbind") return `alias ${p.binding.alias} to stop reaching it`;
    return `alias ${p.binding.alias} to reach it${p.binding.previousProfileId ? ` instead of ${nameOf(p.binding.previousProfileId)}` : ""}`;
  }
  switch (p.kind) {
    case "promotion": return `promotion from ${source}`;
    case "rollback": return "rollback";
    default: return "direct edit";
  }
}
